import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../redis/client.js'
import { createLogger } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'

const logger = createLogger('FuelRegenerationCron')

/**
 * Daily Fuel Regeneration Job
 * Runs at midnight UTC every day to regenerate fuel for all users
 * Uses native Node.js setInterval (no external dependencies)
 */

/**
 * Calculate milliseconds until next midnight UTC
 */
function millisecondsUntilMidnight(): number {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1)
  tomorrow.setUTCHours(0, 0, 0, 0)
  return tomorrow.getTime() - now.getTime()
}

/**
 * Regenerate fuel for all users that haven't been regenerated in 24h
 */
async function regenerateFuelForAllUsers(
  firestore: Firestore,
  redis: RedisClient
): Promise<void> {
  logger.info('Starting daily fuel regeneration...')

  try {
    // Get all users that need fuel regeneration (last regen > 24h ago)
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    
    const usersSnapshot = await firestore
      .collection('users')
      .where('gamification.lastFuelRegeneration', '<=', yesterday)
      .limit(500) // Process in batches
      .get()

    if (usersSnapshot.empty) {
      logger.info('No users need fuel regeneration')
      return
    }

    const batch = createBatchBuilder(firestore)
    const now = new Date()
    let count = 0

    for (const doc of usersSnapshot.docs) {
      const userData = doc.data()
      const maxFuel = userData.gamification?.maxFuel ?? 100

      batch.updateRef(doc.ref, {
        'gamification.fuel': maxFuel,
        'gamification.lastFuelRegeneration': now,
        updatedAt: now
      })

      // Invalidate Redis cache
      await redis.del(`profile:${doc.id}`)

      count++
    }

    await batch.commit()
    logger.info(`Regenerated fuel for ${count} users`)

    // If there are more users, recursively process next batch
    if (usersSnapshot.size === 500) {
      logger.info('Processing next batch...')
      // Schedule next batch processing after 1 minute
      setTimeout(() => {
        regenerateFuelForAllUsers(firestore, redis)
      }, 60000)
    }
  } catch (error) {
    logger.error('Failed to regenerate fuel', { error })
  }
}

/**
 * Register the fuel regeneration job (runs daily at midnight UTC)
 */
export function registerFuelRegenerationJob(
  firestore: Firestore,
  redis: RedisClient
): void {
  // Schedule first run at next midnight
  const msUntilMidnight = millisecondsUntilMidnight()
  logger.info(`Fuel regeneration scheduled in ${Math.floor(msUntilMidnight / 1000 / 60)} minutes`)

  setTimeout(() => {
    // Run immediately at midnight
    regenerateFuelForAllUsers(firestore, redis)

    // Then schedule daily execution at midnight
    setInterval(() => {
      regenerateFuelForAllUsers(firestore, redis)
    }, 24 * 60 * 60 * 1000) // Every 24 hours
  }, msUntilMidnight)
}

